<template>
  <div class="admin-shared-images" :dir="isHebrew ? 'rtl' : 'ltr'">
    <NavBar />
    <div class="container py-5" style="max-width: 800px; padding-top: 100px !important;">
      <h2 class="mb-4">{{ t('sharedImagesTitle') }}</h2>

      <div v-if="loading && !places.length" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2">{{ t('sharedImagesLoading') }}</p>
      </div>

      <div v-else-if="error && !places.length" class="text-center py-5">
        <p class="text-danger">{{ error }}</p>
        <b-button variant="primary" @click="loadPage(1)">{{ t('sharedImagesRetry') }}</b-button>
      </div>

      <p v-else-if="!places.length" class="text-muted text-center py-5">{{ t('sharedImagesEmpty') }}</p>

      <div v-else>
        <b-card v-for="place in places" :key="place.placeId" class="shadow-sm mb-4">
          <h5 class="mb-3">{{ place.placeName || place.placeId }}</h5>
          <div class="image-grid">
            <div
              v-for="image in place.images"
              :key="image.id"
              class="image-item"
              :class="{ approved: image.status === 'approved' }"
            >
              <img :src="image.imageUrl" alt="" class="thumb">
              <b-badge :variant="badgeVariant(image.status)" class="mt-2">
                {{ t(statusKey(image.status)) }}
              </b-badge>
              <div class="d-flex mt-2 actions">
                <b-button
                  size="sm"
                  variant="success"
                  :disabled="busyId === image.id || image.status === 'approved'"
                  @click="act('approve', image.id, place.page)"
                >{{ t('sharedImagesApprove') }}</b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  :disabled="busyId === image.id || image.status === 'rejected'"
                  @click="act('reject', image.id, place.page)"
                >{{ t('sharedImagesReject') }}</b-button>
              </div>
            </div>
          </div>
        </b-card>

        <p v-if="error" class="text-danger error-text">{{ error }}</p>
        <div v-if="hasMore" class="text-center">
          <b-button variant="primary" :disabled="loading" @click="loadPage(lastPage + 1)">
            <b-spinner v-if="loading" small class="mr-1" />
            {{ t('sharedImagesLoadMore') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar';
import api from '@/api/sharedPlaceImages';
import { translationMixin } from '@/mixins/translationMixin';

const INTERNAL_HOME_PATH = '/';
const STATUS_KEYS = {
  approved: 'sharedImagesApproved',
  pending: 'sharedImagesPending',
  rejected: 'sharedImagesRejected',
};
const BADGE_VARIANTS = { approved: 'success', pending: 'warning', rejected: 'danger' };

export default {
  name: 'AdminSharedPlaceImages',
  components: { NavBar },
  mixins: [translationMixin],
  data() {
    return {
      pages: {}, // page number -> places returned for that page
      pageSeq: {}, // page number -> when it was last loaded (higher = newer)
      seq: 0,
      lastPage: 0,
      hasMore: false,
      loading: false,
      busyId: null,
      error: null,
    };
  },
  computed: {
    // Pages in order, each place remembering the page it came from so an
    // action can reload just that page.
    // A place that moved pages after an action can sit on two loaded pages;
    // keep only the copy from the most recently loaded one.
    places() {
      const pageNumbers = Object.keys(this.pages).map(Number).sort((a, b) => a - b);
      const newest = {};
      pageNumbers.forEach((page) => {
        this.pages[page].forEach((p) => {
          if (!newest[p.placeId] || this.pageSeq[page] > this.pageSeq[newest[p.placeId]]) {
            newest[p.placeId] = page;
          }
        });
      });
      return pageNumbers.reduce((all, page) => all.concat(
        this.pages[page]
          .filter((p) => newest[p.placeId] === page)
          .map((p) => ({ ...p, page })),
      ), []);
    },
  },
  created() {
    if (this.$store.getters.userId !== 1) {
      this.$router.push(INTERNAL_HOME_PATH);
      return;
    }
    this.loadPage(1);
  },
  methods: {
    statusKey(status) {
      return STATUS_KEYS[status] || STATUS_KEYS.pending;
    },
    badgeVariant(status) {
      return BADGE_VARIANTS[status] || 'secondary';
    },
    // Fetches one page and stores it; returns the response data or null.
    async loadPage(page) {
      this.loading = true;
      this.error = null;
      try {
        const token = this.$store.state.appauth.apptoken;
        const response = await api.listForReview(token, page);
        if (response.data.ServerMassageCode !== 'success') {
          this.error = this.t('sharedImagesLoadError');
          return null;
        }
        this.seq += 1;
        this.$set(this.pageSeq, page, this.seq);
        this.$set(this.pages, page, response.data.places);
        if (page >= this.lastPage) {
          this.lastPage = page;
          this.hasMore = !!response.data.hasMore;
        }
        return response.data;
      } catch (e) {
        this.error = this.t('sharedImagesLoadError');
        return null;
      } finally {
        this.loading = false;
      }
    },
    async act(action, id, page) {
      this.busyId = id;
      this.error = null;
      let failed = false;
      try {
        const token = this.$store.state.appauth.apptoken;
        const call = action === 'approve' ? api.approveImage : api.rejectImage;
        const response = await call(token, id);
        failed = response.data.ServerMassageCode !== 'success';
      } catch (e) {
        failed = true;
      }
      await this.loadPage(page); // clears this.error first
      if (failed && !this.error) this.error = this.t('sharedImagesActionFailed');
      this.busyId = null;
    },
  },
};
</script>

<style scoped>
/* The site's body aligns right (Hebrew first); start follows the page's dir. */
.admin-shared-images { text-align: start; }
.admin-shared-images h2 { font-size: 28px; }
.admin-shared-images h5 { font-size: 20px; }
.admin-shared-images p,
.admin-shared-images .error-text { font-size: 16px; }
.admin-shared-images .btn { font-size: 14px; }
/* Bootstrap's badge is 75% of a 10px root: too small to read. */
.admin-shared-images .badge { font-size: 13px; padding: 4px 8px; }
.image-grid { display: flex; flex-wrap: wrap; gap: 16px; }
.image-item {
  width: 160px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;
  border: 2px solid transparent;
  border-radius: 8px;
}
.image-item.approved { border-color: #28a745; background: rgba(40, 167, 69, 0.08); }
.thumb { width: 100%; height: 110px; object-fit: cover; border-radius: 4px; }
.actions { gap: 6px; flex-wrap: wrap; }
</style>
